import {debounce} from '@github/mini-throttle'
import {AllShortcutsEnabled} from '../../components/AllShortcutsEnabled'
import {useReposAnalytics} from '../../hooks/use-repos-analytics'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import type {SxProp} from '@primer/styled-react'
import {TextInput} from '@primer/styled-react'
import React from 'react'

import {clsx} from 'clsx'

type Props = {
  ariaActiveDescendant?: string
  ariaControls?: string
  ariaExpanded?: boolean
  ariaHasPopup?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onPreload(): void
  onSearch(query: string): void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  query: string
  className?: string
  ref?: React.Ref<HTMLInputElement>
} & SxProp

export const FilesSearchBox = ({
  ref,
  ariaActiveDescendant,
  ariaControls,
  ariaExpanded,
  ariaHasPopup,
  onBlur,
  onFocus,
  onKeyDown,
  onPreload,
  onSearch,
  query,
  sx,
  className,
}: Props) => {
  const {sendRepoClickEvent} = useReposAnalytics()
  const [textValue, setTextValue] = React.useState(query)
  const skipDebounce = isFeatureEnabled('file_finder_skip_debounce')
  const debouncedOnSearchRef = React.useRef(debounce((newQuery: string) => onSearch(newQuery), 250))

  React.useEffect(() => {
    setTextValue(query)
  }, [query])

  const clearAction = query ? (
    <TextInput.Action
      onClick={() => {
        sendRepoClickEvent('FILE_TREE.CANCEL_SEARCH')
        onSearch('')
      }}
      icon={XCircleFillIcon}
      aria-label="Clear"
      className="fgColor-muted"
    />
  ) : undefined

  return (
    <TextInput
      // support for the search param can be removed once the tree is globally avaialble.
      // We want to focus the search box only if user navigates with ?search=1
      autoFocus={isSearchUrl()}
      ref={ref}
      value={textValue}
      onKeyDown={onKeyDown}
      onChange={e => {
        setTextValue(e.target.value)
        onPreload()
        if (skipDebounce) {
          onSearch(e.target.value)
        } else {
          debouncedOnSearchRef.current(e.target.value)
        }
      }}
      sx={sx}
      className={clsx('d-flex', className)}
      aria-label="Go to file"
      aria-activedescendant={ariaActiveDescendant}
      role={ariaHasPopup ? 'combobox' : undefined}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup ? 'dialog' : undefined}
      autoCorrect="off"
      spellCheck="false"
      placeholder="Go to file"
      leadingVisual={SearchIcon}
      trailingAction={clearAction}
      trailingVisual={
        clearAction
          ? undefined
          : () => (
              <AllShortcutsEnabled>
                <kbd>t</kbd>
              </AllShortcutsEnabled>
            )
      }
      onFocus={e => {
        onPreload()
        e.target.select()
        onFocus?.(e)
      }}
      onBlur={onBlur}
      onClick={() => sendRepoClickEvent('FILE_TREE.SEARCH_BOX')}
    />
  )
}

/**
 * Whether the URL contains ?search=1 which indicates user is searching for a file
 */
export function isSearchUrl() {
  const params = new URLSearchParams(ssrSafeLocation.search)
  return params.get('search') === '1'
}

FilesSearchBox.displayName = 'FilesSearchBox'
